import { useEffect, useRef, useState, KeyboardEvent } from "react";

/**
 * TimerRing — circular count-up / count-down timer component.
 *
 * This implementation covers the UI / UX interactions described in
 * `ref/tools/insession.md` only.  Real-time sync, audio alerts and projector
 * integrations will be wired in the back-end phase.
 */

export type TimerMode = "up" | "down";

interface TimerRingProps {
  /** Starting duration (in seconds) used when `mode === 'down'`. */
  initialSeconds?: number;
  size?: number; // diameter in px
}

const DEFAULT_SIZE = 220;

export default function TimerRing({
  initialSeconds = 5 * 60,
  size = DEFAULT_SIZE,
}: TimerRingProps) {
  const radius = (size - 16) / 2; // leave 8 px padding on either side
  const circumference = 2 * Math.PI * radius;

  // internal state --------------------------------------------------
  const [mode, setMode] = useState<TimerMode>("down");
  const [duration, setDuration] = useState(initialSeconds);
  const [elapsed, setElapsed] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  // refs to preserve latest values inside interval callback
  const isRunningRef = useRef(isRunning);
  const modeRef = useRef(mode);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // ticker ----------------------------------------------------------
  useEffect(() => {
    const tick = () => {
      if (!isRunningRef.current) return;
      setElapsed((prev) => {
        const next = prev + 1;
        if (modeRef.current === "down" && next >= duration) {
          // reached zero — pause and clamp elapsed
          setIsRunning(false);
          return duration;
        }
        return next;
      });
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [duration]);

  // keyboard shortcuts ---------------------------------------------
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "s":
      case "S":
        toggle();
        break;
      case "r":
      case "R":
        reset();
        break;
      case "ArrowUp":
        adjust(60);
        break;
      case "ArrowDown":
        adjust(-60);
        break;
    }
  };

  // helper fns ------------------------------------------------------
  const toggle = () => setIsRunning((v) => !v);
  const reset = () => {
    setIsRunning(false);
    setElapsed(0);
  };
  const adjust = (delta: number) => {
    setDuration((d) => Math.max(0, d + delta));
  };

  const toggleMode = () => {
    setMode((m) => (m === "down" ? "up" : "down"));
    reset();
  };

  // derived values --------------------------------------------------
  const secondsLeft = mode === "down" ? Math.max(duration - elapsed, 0) : elapsed;
  const progressRatio = (() => {
    if (mode === "up") {
      // up mode: we don’t know the final duration; visualise elapsed vs preset (if any)
      return (elapsed % duration) / duration;
    }
    return elapsed / duration;
  })();

  const dashOffset = circumference * (1 - progressRatio);

  // colour logic – warn when ≤ 10 % time remaining (count-down only)
  const isWarn = mode === "down" && progressRatio >= 0.9;

  const formatted = new Intl.DateTimeFormat("en-GB", {
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(secondsLeft * 1000));

  return (
    <div
      className="flex flex-col items-center gap-4 select-none"
      tabIndex={0}
      onKeyDown={handleKey}
    >
      {/* SVG ring -------------------------------------------------- */}
      <svg
        width={size}
        height={size}
        className="overflow-visible"
      >
        {/* track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="fill-none stroke-gray-300 dark:stroke-gray-700"
          strokeWidth={12}
        />
        {/* progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={
            "fill-none transition-colors duration-300 " +
            (isWarn ? "stroke-[color:var(--c-warn,#F59E0B)]" : "stroke-primary")
          }
          strokeWidth={12}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
        />

        {/* centre label */}
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="text-2xl font-semibold fill-gray-900 dark:fill-gray-100 select-none"
        >
          {formatted}
        </text>
      </svg>

      {/* controls – educator only for now -------------------------- */}
      <div className="flex gap-2">
        <button
          onClick={toggle}
          className="rounded-md bg-primary px-3 py-1.5 text-white hover:bg-primary-dark focus:outline-hidden focus:ring-3 focus:ring-primary"
        >
          {isRunning ? "Pause" : "Start"}
        </button>
        <button
          onClick={reset}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-3 focus:ring-primary"
        >
          Reset
        </button>
        <button
          onClick={() => adjust(60)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-3 focus:ring-primary"
        >
          +1 m
        </button>
        <button
          onClick={toggleMode}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-3 focus:ring-primary"
        >
          {mode === "down" ? "Count Up" : "Count Down"}
        </button>
      </div>
    </div>
  );
}
